import { isNavGroup, type NavZone } from "./navItems";

/** Separates the zone / section / group parts of a result's context line. */
export const CONTEXT_SEPARATOR = " › ";

export type NavSearchHit = {
  path: string;
  label: string;
  badge?: number;
  /**
   * Where the item lives, e.g. "Platform › Operations". Two items are called
   * "Users", so a bare label cannot identify a result.
   */
  context: string;
  /** Lets a result reopen its section after navigating. */
  sectionId: string;
  /** Lower-cased label + context, matched against as one string. */
  haystack: string;
};

/**
 * Flattens the *built* nav into searchable destinations.
 *
 * Takes the gated zones rather than the raw definition, so search can only ever
 * surface what this user can already see — permissions and feature flags are
 * respected for free, and there is no second list to keep in step.
 */
export function flattenNav(zones: NavZone[]): NavSearchHit[] {
  const hits: NavSearchHit[] = [];

  const add = (path: string, label: string, context: string, sectionId: string, badge?: number) => {
    hits.push({
      path,
      label,
      context,
      sectionId,
      badge,
      haystack: `${label} ${context}`.toLowerCase(),
    });
  };

  for (const zone of zones) {
    for (const section of zone.sections) {
      // A section without children is a destination itself (Home, Data Marts).
      if (!section.children) {
        if (section.path) add(section.path, section.label, zone.label ?? "", section.id);
        continue;
      }

      const sectionContext = [zone.label, section.label].filter(Boolean).join(CONTEXT_SEPARATOR);

      for (const child of section.children) {
        if (isNavGroup(child)) {
          const groupContext = [sectionContext, child.groupTitle].join(CONTEXT_SEPARATOR);
          for (const item of child.items) {
            add(item.path, item.label, groupContext, section.id, item.badge);
          }
          continue;
        }
        add(child.path, child.label, sectionContext, section.id, child.badge);
      }
    }
  }

  return hits;
}

/**
 * Case-insensitive substring match over label + context. Every whitespace
 * separated token must appear somewhere, so "ops" finds everything under
 * Operations and "slug backfill" narrows to the backfills.
 */
export function filterNav(hits: NavSearchHit[], query: string): NavSearchHit[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  return hits.filter(hit => tokens.every(token => hit.haystack.includes(token)));
}
